/**
 * Link Intra-Group Site Modal
 *
 * Lets an admin link "site A supplies site B" for operating-org sites.
 * The backend resolves (or creates) the buying site's customer-typed twin
 * company and creates the CustomerSupplierRelationship, so the link shows
 * up exactly like normal customer/supplier records.
 */
import { useEffect, useState } from 'react';
import { coreAPI, Company } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Loader2, Link2 } from 'lucide-react';
import { toast } from 'sonner';

interface LinkIntraSiteModalProps {
  /** Controls modal visibility. */
  open: boolean;
  /** Open/close callback (wired to Dialog onOpenChange). */
  onOpenChange: (open: boolean) => void;
  /** Called after a successful link so the caller can refresh its list. */
  onLinked: () => void;
}

export function LinkIntraSiteModal({ open, onOpenChange, onLinked }: LinkIntraSiteModalProps) {
  const [sites, setSites] = useState<Company[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [supplierSiteId, setSupplierSiteId] = useState<string>('');
  const [buyerSiteId, setBuyerSiteId] = useState<string>('');
  const [bothDirections, setBothDirections] = useState(false);
  const [erpCode, setErpCode] = useState('');
  const [reverseErpCode, setReverseErpCode] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSupplierSiteId('');
    setBuyerSiteId('');
    setBothDirections(false);
    setErpCode('');
    setReverseErpCode('');
    setSitesLoading(true);
    coreAPI.getAccessibleCompanies({ type: 'supplier' })
      .then((response) => setSites(response.data.results ?? []))
      .catch((err: unknown) => {
        console.error('Failed to load sites:', err);
        toast.error('Failed to load sites');
      })
      .finally(() => setSitesLoading(false));
  }, [open]);

  const handleSave = async () => {
    if (!supplierSiteId || !buyerSiteId) {
      toast.error('Select both a supplying site and a buying site');
      return;
    }
    if (supplierSiteId === buyerSiteId) {
      toast.error('Supplying site and buying site must be different');
      return;
    }
    setSaving(true);
    try {
      const response = await coreAPI.linkIntraSite({
        supplier_site_id: Number(supplierSiteId),
        buyer_site_id: Number(buyerSiteId),
        both_directions: bothDirections,
        erp_code: erpCode.trim() || undefined,
        reverse_erp_code: bothDirections ? (reverseErpCode.trim() || undefined) : undefined,
      });
      const codeErrors = response.data.directions
        .map((d) => d.erp_code_error)
        .filter((e): e is string => Boolean(e));
      codeErrors.forEach((e) => toast.warning(e));
      const summary = response.data.directions
        .map((d) => `${d.supplier_site_name} → ${d.twin_name}`)
        .join('; ');
      toast.success(`Linked ${summary}`);
      onOpenChange(false);
      onLinked();
    } catch (err: unknown) {
      console.error('Failed to link sites:', err);
      const message = err instanceof Error ? err.message : 'Failed to link sites';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Link Intra-Group Site
          </DialogTitle>
          <DialogDescription>
            Make one of your sites visible as a supplier of another. The link
            appears exactly like a normal customer/supplier record.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Supplying site <span className="text-red-500">*</span></Label>
            <Select value={supplierSiteId} onValueChange={setSupplierSiteId}>
              <SelectTrigger data-testid="intra-supplying-site">
                <SelectValue placeholder={sitesLoading ? 'Loading sites...' : 'Select supplying site'} />
              </SelectTrigger>
              <SelectContent>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={String(site.id)}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Buying site <span className="text-red-500">*</span></Label>
            <Select value={buyerSiteId} onValueChange={setBuyerSiteId}>
              <SelectTrigger data-testid="intra-buying-site">
                <SelectValue placeholder={sitesLoading ? 'Loading sites...' : 'Select buying site'} />
              </SelectTrigger>
              <SelectContent>
                {sites.map((site) => (
                  <SelectItem key={site.id} value={String(site.id)}>
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="intra-erp-code">
              ERP customer code <span className="text-muted-foreground text-xs">(how DNA resolves the buying site — same as normal customers)</span>
            </Label>
            <Input
              id="intra-erp-code"
              data-testid="intra-erp-code"
              value={erpCode}
              onChange={(e) => setErpCode(e.target.value.toUpperCase())}
              placeholder="e.g. POE002"
              maxLength={20}
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="intra-both-directions"
              checked={bothDirections}
              onCheckedChange={(checked) => setBothDirections(checked === true)}
            />
            <Label htmlFor="intra-both-directions" className="font-normal cursor-pointer">
              Link both directions
            </Label>
          </div>

          {bothDirections && (
            <div className="space-y-2">
              <Label htmlFor="intra-reverse-erp-code">
                Reverse ERP code <span className="text-muted-foreground text-xs">(code for the supplying site's twin)</span>
              </Label>
              <Input
                id="intra-reverse-erp-code"
                data-testid="intra-reverse-erp-code"
                value={reverseErpCode}
                onChange={(e) => setReverseErpCode(e.target.value.toUpperCase())}
                placeholder="e.g. POE001"
                maxLength={20}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || sitesLoading}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
